/**
 * Business Hours Helper Functions
 *
 * Utility functions for handling business hours in both structured format
 * and human-readable text format.
 */

// Spanish day names mapping
const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

// Day key to label mapping for quick lookups
const DAY_LABELS = DAYS.reduce((acc, day) => {
  acc[day.key] = day.label;
  return acc;
}, {});

/**
 * Converts 24-hour time format to 12-hour AM/PM format
 * @param {string} time24 - Time in 24-hour format (e.g., "13:30")
 * @returns {string} Time in 12-hour format (e.g., "1:30 PM")
 */
export function formatTime(time24) {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

/**
 * Converts 12-hour AM/PM time format to 24-hour format
 * @param {string} time12 - Time in 12-hour format (e.g., "1:30 PM")
 * @returns {string} Time in 24-hour format (e.g., "13:30")
 */
export function convertTo24Hour(time12) {
  const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return time12; // Return as-is if format doesn't match

  let [, hour, minutes, ampm] = match;
  hour = parseInt(hour, 10);

  if (ampm.toUpperCase() === 'PM' && hour !== 12) {
    hour += 12;
  } else if (ampm.toUpperCase() === 'AM' && hour === 12) {
    hour = 0;
  }

  return `${hour.toString().padStart(2, '0')}:${minutes}`;
}

/**
 * Checks if a schedule represents 24/7 availability
 * @param {Object} structured - Business hours in structured format
 * @returns {boolean} True if schedule is 24/7
 */
export function is24x7Schedule(structured) {
  if (!structured || typeof structured !== 'object') {
    return false;
  }

  // Check if all days are enabled with 00:00 - 23:59
  return DAYS.every(
    day => structured[day.key]?.enabled &&
           structured[day.key]?.start === '00:00' &&
           structured[day.key]?.end === '23:59'
  );
}

/**
 * Generates human-readable text from structured business hours
 * @param {Object} structured - Business hours in structured format
 * @returns {string} Human-readable text representation
 */
export function generateBusinessHoursText(structured) {
  if (!structured || typeof structured !== 'object') {
    return 'Sin horario configurado';
  }

  // Check for 24/7 schedule
  if (is24x7Schedule(structured)) {
    return '24/7 - Disponible las 24 horas, todos los días';
  }

  // Get enabled days in order
  const enabledDays = DAYS.filter(day => structured[day.key]?.enabled);

  if (enabledDays.length === 0) {
    return 'Sin horario configurado';
  }

  // Group consecutive days with same hours
  const groups = [];
  let currentGroup = null;

  enabledDays.forEach((day, index) => {
    const hours = structured[day.key];
    const timeRange = `${formatTime(hours.start)} - ${formatTime(hours.end)}`;

    if (!currentGroup || currentGroup.timeRange !== timeRange) {
      // Start new group
      if (currentGroup) {
        groups.push(currentGroup);
      }
      currentGroup = {
        startDay: day.label,
        endDay: day.label,
        timeRange,
        dayKeys: [day.key],
      };
    } else {
      // Check if this day is consecutive to the last day in the group
      const lastDayIndex = DAYS.findIndex(d => d.key === currentGroup.dayKeys[currentGroup.dayKeys.length - 1]);
      const currentDayIndex = DAYS.findIndex(d => d.key === day.key);

      if (currentDayIndex === lastDayIndex + 1) {
        // Extend current group (consecutive day)
        currentGroup.endDay = day.label;
        currentGroup.dayKeys.push(day.key);
      } else {
        // Start new group (non-consecutive day with same hours)
        groups.push(currentGroup);
        currentGroup = {
          startDay: day.label,
          endDay: day.label,
          timeRange,
          dayKeys: [day.key],
        };
      }
    }

    // Push last group
    if (index === enabledDays.length - 1) {
      groups.push(currentGroup);
    }
  });

  // Format groups into text
  return groups
    .map(group => {
      const dayRange =
        group.startDay === group.endDay
          ? group.startDay
          : `${group.startDay} a ${group.endDay}`;
      return `${dayRange}: ${group.timeRange}`;
    })
    .join(', ');
}

/**
 * Returns default business hours configuration
 * Monday-Friday: 9:00-18:00 (enabled)
 * Saturday-Sunday: disabled
 * @returns {Object} Default business hours structure
 */
export function getDefaultBusinessHours() {
  return {
    monday: { enabled: true, start: '09:00', end: '18:00' },
    tuesday: { enabled: true, start: '09:00', end: '18:00' },
    wednesday: { enabled: true, start: '09:00', end: '18:00' },
    thursday: { enabled: true, start: '09:00', end: '18:00' },
    friday: { enabled: true, start: '09:00', end: '18:00' },
    saturday: { enabled: false, start: '09:00', end: '18:00' },
    sunday: { enabled: false, start: '09:00', end: '18:00' },
  };
}

/**
 * Creates a 24/7 business hours configuration
 * @returns {Object} 24/7 business hours structure
 */
export function get24x7BusinessHours() {
  const schedule = {};
  DAYS.forEach(({ key }) => {
    schedule[key] = {
      enabled: true,
      start: '00:00',
      end: '23:59',
    };
  });
  return schedule;
}

/**
 * Validates business hours structure
 * @param {Object} structured - Business hours in structured format
 * @returns {Object} Validation result { valid: boolean, errors: array }
 */
export function validateBusinessHours(structured) {
  const errors = [];

  // Check if structured object exists
  if (!structured || typeof structured !== 'object') {
    errors.push('El horario debe ser un objeto válido');
    return { valid: false, errors };
  }

  // Check if it's 24/7 (no further validation needed)
  if (is24x7Schedule(structured)) {
    return { valid: true, errors: [] };
  }

  // Validate each day
  const dayErrors = {};
  DAYS.forEach(({ key, label }) => {
    const day = structured[key];

    // Check if day object exists
    if (!day || typeof day !== 'object') {
      dayErrors[key] = `${label}: Configuración inválida`;
      return;
    }

    // If day is enabled, validate times
    if (day.enabled) {
      // Check if start and end times exist
      if (!day.start || !day.end) {
        dayErrors[key] = `${label}: Debe especificar hora de inicio y fin`;
        return;
      }

      // Validate time format (HH:MM)
      const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(day.start)) {
        dayErrors[key] = `${label}: Hora de inicio inválida (formato: HH:MM)`;
        return;
      }
      if (!timeRegex.test(day.end)) {
        dayErrors[key] = `${label}: Hora de cierre inválida (formato: HH:MM)`;
        return;
      }

      // Validate that end time is after start time
      if (day.start >= day.end) {
        dayErrors[key] = `${label}: La hora de cierre debe ser posterior a la de apertura`;
        return;
      }
    }
  });

  // Check if at least one day is enabled
  const hasEnabledDay = DAYS.some(({ key }) => structured[key]?.enabled);
  if (!hasEnabledDay) {
    errors.push('Debe habilitar al menos un día de la semana');
  }

  // Combine day-specific errors with general errors
  Object.values(dayErrors).forEach(error => errors.push(error));

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Normalizes business hours input to structured format
 * Handles various input formats and ensures consistent output
 * @param {Object|string} input - Business hours input (structured or text)
 * @returns {Object|null} Normalized business hours structure or null if invalid
 */
export function normalizeBusinessHours(input) {
  // If input is null or undefined, return default
  if (!input) {
    return getDefaultBusinessHours();
  }

  // If input is already structured, validate and return
  if (typeof input === 'object') {
    const validation = validateBusinessHours(input);
    if (validation.valid) {
      return input;
    }
  }

  // If input is text, we can't reliably parse it, so return default
  // (Parsing natural language schedules is complex and error-prone)
  if (typeof input === 'string') {
    console.warn('Text-based business hours parsing not supported. Returning default schedule.');
    return getDefaultBusinessHours();
  }

  return null;
}

/**
 * Checks if a given time falls within business hours for a specific day
 * @param {Object} businessHours - Business hours structure
 * @param {string} dayKey - Day key (e.g., 'monday')
 * @param {string} time24 - Time in 24-hour format (e.g., '14:30')
 * @returns {boolean} True if time is within business hours
 */
export function isTimeWithinBusinessHours(businessHours, dayKey, time24) {
  if (!businessHours || !dayKey || !time24) {
    return false;
  }

  // Check if it's 24/7
  if (is24x7Schedule(businessHours)) {
    return true;
  }

  const day = businessHours[dayKey];
  if (!day || !day.enabled) {
    return false;
  }

  return time24 >= day.start && time24 <= day.end;
}

/**
 * Gets the day key for a given Date object
 * @param {Date} date - Date object
 * @returns {string} Day key (e.g., 'monday')
 */
export function getDayKeyFromDate(date) {
  const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return dayKeys[dayIndex];
}

/**
 * Checks if a given Date is within business hours
 * @param {Object} businessHours - Business hours structure
 * @param {Date} date - Date object to check
 * @returns {boolean} True if date/time is within business hours
 */
export function isDateWithinBusinessHours(businessHours, date) {
  if (!businessHours || !date) {
    return false;
  }

  const dayKey = getDayKeyFromDate(date);
  const time24 = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;

  return isTimeWithinBusinessHours(businessHours, dayKey, time24);
}

/**
 * Copies schedule from one day to all other days
 * @param {Object} businessHours - Business hours structure
 * @param {string} sourceDayKey - Day key to copy from (default: 'monday')
 * @returns {Object} New business hours structure with copied schedule
 */
export function copyScheduleToAllDays(businessHours, sourceDayKey = 'monday') {
  if (!businessHours || !businessHours[sourceDayKey]) {
    return businessHours;
  }

  const sourceSchedule = businessHours[sourceDayKey];
  const newSchedule = {};

  DAYS.forEach(({ key }) => {
    newSchedule[key] = { ...sourceSchedule };
  });

  return newSchedule;
}

/**
 * Gets a summary of business hours (total hours per week)
 * @param {Object} businessHours - Business hours structure
 * @returns {Object} Summary with total hours and enabled days count
 */
export function getBusinessHoursSummary(businessHours) {
  if (!businessHours) {
    return { totalHoursPerWeek: 0, enabledDaysCount: 0 };
  }

  // Check if 24/7
  if (is24x7Schedule(businessHours)) {
    return { totalHoursPerWeek: 168, enabledDaysCount: 7, is24x7: true };
  }

  let totalMinutes = 0;
  let enabledDaysCount = 0;

  DAYS.forEach(({ key }) => {
    const day = businessHours[key];
    if (day?.enabled) {
      enabledDaysCount++;

      // Calculate hours for this day
      const [startHour, startMin] = day.start.split(':').map(Number);
      const [endHour, endMin] = day.end.split(':').map(Number);

      const startTotalMinutes = startHour * 60 + startMin;
      const endTotalMinutes = endHour * 60 + endMin;

      totalMinutes += (endTotalMinutes - startTotalMinutes);
    }
  });

  return {
    totalHoursPerWeek: Math.round((totalMinutes / 60) * 10) / 10, // Round to 1 decimal
    enabledDaysCount,
    is24x7: false,
  };
}

// Export DAYS array for use in components
export { DAYS, DAY_LABELS };
